import type React from "react";

export type NumberInputMode = "integer" | "decimal2";

type CreateInputNumberGuardsOptions = {
  mode: NumberInputMode;
};

/**
 * ✅ Guard สำหรับ AntD InputNumber เพื่อ "กันตั้งแต่แรก" ไม่ให้พิมพ์ตัวอักษร/สัญลักษณ์ที่ไม่เกี่ยวข้อง
 * - ไม่รองรับค่าติดลบ (ตาม requirement)
 * - รองรับทั้ง integer และ decimal (จำกัด 2 ตำแหน่ง) ผ่าน option `mode`
 *
 * ใช้ร่วมกับ `parser`/`precision` ได้ (parser เป็น safety net)
 */
export function createInputNumberGuards({ mode }: CreateInputNumberGuardsOptions) {
  const allowDecimal = mode === "decimal2";

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    const key = e.key;

    // Allow common navigation/edit keys
    const allowedControlKeys = new Set([
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ]);
    if (allowedControlKeys.has(key)) return;

    // Allow shortcuts: Ctrl/Cmd + A/C/V/X (select/copy/paste/cut)
    if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(key.toLowerCase())) return;

    // Disallow negative sign explicitly
    if (key === "-") {
      e.preventDefault();
      return;
    }

    // Allow digits
    if (/^\d$/.test(key)) return;

    // Allow single decimal dot (only in decimal mode)
    if (allowDecimal && key === ".") {
      const input = e.currentTarget;
      const value = input.value ?? "";
      if (value.includes(".")) {
        e.preventDefault();
        return;
      }
      return;
    }

    // Block everything else (letters, spaces, symbols)
    e.preventDefault();
  };

  const onPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;

    const normalized = text.trim();

    // Allow paste only if it's a valid numeric pattern for the mode (non-negative)
    const intOk = /^\d+$/.test(normalized);
    const decOk = /^\d+(\.\d{0,2})?$/.test(normalized);

    const ok = allowDecimal ? decOk : intOk;
    if (!ok) {
      e.preventDefault();
    }
  };

  return { onKeyDown, onPaste };
}


